import { supabase } from './supabaseClient';

export const authService = {
  async signUp(email, password, metadata = {}) {
    const { data, error } = await supabase.auth.signUp({
      email, password,
      options: {
        data: metadata,
        emailRedirectTo: `${window.location.origin}/dashboard`
      }
    });
    if (error) throw error;

    // If Supabase returned a session (email confirmation OFF), we're authenticated
    // and can create the customer profile with the user's JWT.
    // If no session (email confirmation ON), skip profile creation here —
    // it will be created on first sign-in via AuthContext.
    if (data.session && data.user) {
      await this.createCustomerProfile(data.user.id, email, metadata);
    }
    return data;
  },

  async signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  },

  async signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  async getSession() {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) throw error;
    return session;
  },

  async getUser() {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  },

  async resetPassword(email) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    });
    if (error) throw error;
  },

  async updatePassword(newPassword) {
    const { data, error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
    return data;
  },

  async createCustomerProfile(authUserId, email, metadata) {
    try {
      const { error } = await supabase.from('customers').insert({
        auth_user_id: authUserId,
        email,
        first_name: metadata.first_name || '',
        last_name: metadata.last_name || '',
        company_name: metadata.company || metadata.company_name || '',
        phone: metadata.phone || '',
        address: metadata.address || '',
        city: metadata.city || '',
        state: metadata.state || '',
        zip: metadata.zip || '',
        billing_address: metadata.billing_address || metadata.address || '',
        billing_city: metadata.billing_city || metadata.city || '',
        billing_state: metadata.billing_state || metadata.state || '',
        billing_zip: metadata.billing_zip || metadata.zip || '',
        email_verified: false,
        registration_status: 'registration_started',
      });
      if (error && error.code !== '23505') {
        console.error('Error creating customer profile:', error.message);
      }
    } catch (err) {
      console.error('Exception creating customer profile:', err.message);
    }
  },

  async getCustomerProfile() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return null;
      const { data, error } = await supabase
        .from('customers').select('*')
        .eq('auth_user_id', session.user.id).single();
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    } catch (err) {
      console.error('getCustomerProfile error:', err.message);
      throw err;
    }
  },

  async updateCustomerProfile(updates) {
    const user = await authService.getUser();
    if (!user) throw new Error('Not authenticated');
    const { data, error } = await supabase
      .from('customers').update(updates)
      .eq('auth_user_id', user.id).select().single();
    if (error) throw error;
    return data;
  },

  async verifyEmail(token) {
    const response = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/verify-email`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      }
    );
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Verification failed');
    }
    return response.json();
  },

  async resendVerificationEmail(customerId) {
    const token = await this.getSession().then(s => s?.access_token);
    const response = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/handle-payment-success`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'resend-verification', customerId }),
      }
    );
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Failed to resend verification email');
    }
    return response.json();
  },

  onAuthStateChange(callback) {
    return supabase.auth.onAuthStateChange(callback);
  }
};

export default authService;
